/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FontIcon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { defineMessages } from 'react-intl';
import { GaugeChart } from 'src/core/components/GaugeChart';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { ChartAxisData } from 'src/core/types/chart';
import { ModelEvaluationStore } from 'src/modules/modelDetails/stores/ModelEvaluationStore';

export type ModelDetailsCardProps = {
    trainModelInfo: { lastTrainingDate: Date; lastTrainingDuration: number };
    modelEvaluationStore: ModelEvaluationStore;
};

type PerformanceMetric = 'precision' | 'recall' | 'f1Score';

const messages = defineMessages({
    status: {
        id: 'modelDetailsCard.status',
        defaultMessage: 'Status'
    },
    trainingInProgress: {
        id: 'modelDetailsCard.trainingInProgress',
        defaultMessage: 'Training in progress'
    },
    dataProcessing: {
        id: 'modelDetailsCard.dataProcessing',
        defaultMessage: 'Data processing'
    },
    trainingTime: {
        id: 'modelDetailsCard.trainingTime',
        defaultMessage: 'Training model will take a few hours.'
    },
    trainedSuccessfully: {
        id: 'modelDetailsCard.trainedSuccessfully',
        defaultMessage: 'Trained successfully'
    },
    trainingFailed: {
        id: 'modelDetailsCard.trainingFailed',
        defaultMessage: 'Training Failed'
    },
    finishedTrainingOn: {
        id: 'modelDetailsCard.finishedTrainingOn',
        defaultMessage: 'Finished training on'
    },
    trainingStarted: {
        id: 'modelDetailsCard.trainingStarted',
        defaultMessage: 'Training started'
    },
    totalTrainingTime: {
        id: 'modelDetailsCard.totalTrainingTime',
        defaultMessage: 'Total training time'
    },
    numberOfTrainingDocuments: {
        id: 'modelDetailsCard.numberOfTrainingDocuments',
        defaultMessage: 'Number of training documents'
    },
    numberOfTestingDocuments: {
        id: 'modelDetailsCard.numberOfTestingDocuments',
        defaultMessage: 'Number of testing documents'
    },
    precision: {
        id: 'modelDetailsCard.precision',
        defaultMessage: 'Precision'
    },
    recall: {
        id: 'modelDetailsCard.recall',
        defaultMessage: 'Recall'
    },
    f1Score: {
        id: 'modelDetailsCard.f1Score',
        defaultMessage: 'F1 score'
    },
    timeString: {
        id: 'modelDetailsCard.timeString',
        defaultMessage: '{ hours } hour(s), { minutes } minute(s), { seconds } second(s)'
    },
    trainingDataSplittingType: {
        id: 'modelDetailsCard.trainingDataSplittingType',
        defaultMessage: 'Training data splitting type'
    },
    percentageType: {
        id: 'modelDetailsCard.percentageType',
        defaultMessage: 'Percentage'
    },
    manualType: {
        id: 'modelDetailsCard.manualType',
        defaultMessage: 'Manual'
    }
});

const theme = getScopedTheme('ModelDetailsCard');

const { styleComponent } = getStylist('ModelDetailsCard');

const Card = styleComponent<IStackProps>(Stack)('ModelDetailsCard', {
    borderWidth: '1px',
    borderStyle: 'solid',
    boxShadow: theme.boxShadow,
    padding: '12px 24px 24px 24px',
    borderColor: theme.borderColor
});

/**
 * Displays the details of model trining status and performance metrics
 * when the training is done.
 */
export const ModelDetailsCard = (props: ModelDetailsCardProps) => {
    const { trainModelInfo, modelEvaluationStore } = props;
    const { localize, locale } = useLocalization();
    const { isXXLargeOrGreater, isXLargeOrGreater } = useResponsiveMode();

    const performanceMetrics: PerformanceMetric[] = ['precision', 'recall', 'f1Score'];

    const secondsToTimeString = (s: number) => {
        const seconds = s % 60;
        s = (s - seconds) / 60;
        const minutes = s % 60;
        const hours = (s - minutes) / 60;

        return localize(messages.timeString, { hours, minutes, seconds });
    };

    const renderPerformanceMetrics = (metric: PerformanceMetric, isDisabled: boolean = false) => {
        const xData: ChartAxisData<string> = {
            label: 'Metric',
            data: [metric]
        };
        const yData: ChartAxisData<number> = {
            label: 'Value',
            data: [modelEvaluationStore ? modelEvaluationStore[metric] * 100 : 0]
        };

        return (
            <Stack tabIndex={0} key={metric} horizontalAlign="center">
                <LuisText aria-label={`${localize(messages[metric])} = ${yData.data}%`} fontWeight={600}>
                    {localize(messages[metric])}
                </LuisText>
                <GaugeChart color={isDisabled ? [theme.neutral] : [theme[metric]]} xData={xData} yData={yData} />
            </Stack>
        );
    };

    return (
        <Card tokens={{ childrenGap: 25 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                <LuisText fontWeight={600} variant="large">
                    {localize(messages.status)}:
                </LuisText>

                <FontIcon iconName="CompletedSolid" data-automation-id="trainedSuccessfully" style={{ color: 'green' }} />
                <LuisText fontWeight={600} variant="large">
                    {localize(messages.trainedSuccessfully)}
                </LuisText>
            </Stack>

            <Stack horizontal={isXXLargeOrGreater} tokens={{ childrenGap: 60 }}>
                <LuisText fontWeight={600}>
                    {localize(messages.finishedTrainingOn)}
                    :&nbsp;
                    <LuisText>{trainModelInfo.lastTrainingDate.toLocaleString(locale)}</LuisText>
                    <br />
                    <br />
                    {localize(messages.totalTrainingTime)}
                    :&nbsp;
                    <LuisText>{secondsToTimeString(trainModelInfo.lastTrainingDuration)}</LuisText>
                    <br />
                    <br />
                    {localize(messages.trainingDataSplittingType)}
                    :&nbsp;
                    <LuisText>
                        {localize(modelEvaluationStore.trainingSplitType === 'percentage' ? messages.percentageType : messages.manualType)}
                    </LuisText>
                </LuisText>

                <Stack.Item align="center" styles={{ root: { flex: 1 } }}>
                    <Stack horizontal={isXLargeOrGreater} horizontalAlign="space-evenly" tokens={isXLargeOrGreater && { childrenGap: 20 }}>
                        {performanceMetrics.map(metric => renderPerformanceMetrics(metric, false))}
                    </Stack>
                </Stack.Item>
            </Stack>
        </Card>
    );
};
